(ns ui.subscription.page
  (:require [app.client-log :as client-log]
            [storrito.ui.core :as ui]
            [ui.app-frame :as app-frame]
            [parts.replicant.query :as query]
            [ui.subscription.buy :as buy]
            [parts.replicant.command :as command]
            [ui.elements]
            [ui.subscription.part :as part]
            [ui.subscription.core :as core]))

(defn subscription-overview
  [state]
  (let [subscription (query/get-result state
                                       core/subscription-query)
        current-state (-> state
                          (query/get-result core/user-session-query))
        cancellation-scheduled (:scheduled_change (:organization/paddle-subscription subscription))
        status (:status (:organization/paddle-subscription subscription))
        active (and (#{"active" "past_due"} status)
                    (not cancellation-scheduled))]
    (when (:organization/paddle-subscription subscription)
      [:div {:class (conj ui.elements/panel
                          "max-w-2xl")}
       ;; Header: Plan name, monthly cost, and status badge
       (when (or active
                 cancellation-scheduled)
         [:div {:class "flex items-center justify-between mb-4"}
          [:div
           [:h2 {:class "text-xl font-semibold text-gray-800"} "Automatic Top-Up"]
           (when active
             [:p {:class "text-sm text-gray-500"}
              (let [item (first (:items (:organization/paddle-subscription subscription)))]
                (str "$"
                     (:quantity item)
                     " / "
                     (get-in item
                             [:price
                              :billing_cycle
                              :interval])))
              [:span {:class ["ml-1" "text-sm" "text-gray-500"]}
               "(next payment on "
               (let [next-transaction (:next_transaction
                                        (:organization/paddle-subscription subscription))
                     billing-period (:billing_period next-transaction)]
                 (subs (or (:starts_at billing-period)
                           "")
                       0 10))
               ")"]])]

          [:span {:class ["inline-block"
                          (if cancellation-scheduled
                            "bg-orange-300"
                            "bg-green-300")
                          (if cancellation-scheduled
                            "text-red-700"
                            "text-green-700"
                            )
                          "text-sm"
                          "font-medium"
                          "px-4"
                          "py-2"
                          "rounded-full"]}
           (if cancellation-scheduled
             "cancellation scheduled"
             status)]])

       ;; Key Details Grid
       [:div {:class "grid grid-cols-2 gap-4 text-sm text-gray-700 mb-6"}
        ;; Credit balance
        [:div
         [:div {:class "font-medium text-gray-500"} "Credit Balance"]
         (:credit-balance-formatted current-state)]
        ;; Subscription ID
        (when active
          [:div
           [:div {:class "font-medium text-gray-500"} "Subscription ID"]
           [:div (:id (:organization/paddle-subscription subscription))]])
        ;; Billing email
        (when (or active
                  cancellation-scheduled)
          [:div
           [:div {:class "font-medium text-gray-500"} "Billing Email"]
           [:div (:email (:customer subscription))]])
        ;; Payment method
        (when active
          [:div
           [:div {:class "font-medium text-gray-500"} "Payment Method"]
           (let [{:keys [subscription/payment-method-details]} subscription]
             [:div
              (case (:type payment-method-details)
                "card" (let [card (:card payment-method-details)]
                         [:span
                          [:span
                           (let [classes ["fab" "fa-xl" "w-7" "text-gray-500"]
                                 type->class {"visa" "fa-cc-visa"
                                              "master" "fa-cc-mastercard"
                                              "american_express" "fa-cc-amex"
                                              "discover" "fa-cc-discover"
                                              "jcb" "fa-cc-jcb"
                                              "diners_club" "fa-cc-diners-club"
                                              "unionpay" "fa-cc-master"
                                              nil "fa-credit-card"}]
                             [:i {:class (cons (type->class (:type card))
                                               classes)}])
                           "Card (" (:last4 card) ")"]

                          [:span.ml-2
                           [:i.fas.fa-clock.text-gray-500.w-4]
                           (:expiry_month card) "/" (:expiry_year card)]])

                "paypal" [:span [:i.fa-brands.fa-cc-paypal.fa-2xl.w-10.text-gray-500]
                          "Paypal"]
                "alipay" [:span [:i.fa-brands.fa-alipay.fa-2xl.w-10.text-gray-500]
                          "Alipay"]
                "apple_pay" [:span [:i.fa-brands.fa-cc-apple-pay.fa-2xl.w-10.text-gray-500]
                             "Apple Pay"]
                "google_pay" [:span [:i.fa-brands.fa-google-pay.fa-2xl.w-10.text-gray-500]
                              "Google Pay"]
                "wire_transfer" [:span "Wire Transfer"]

                [:span (:type payment-method-details)])])])]

       (when cancellation-scheduled
         [:div {:class "flex items-center space-x-3 mb-4"}
          [:button {:class (conj ui/button-primary)
                    :on {:click [[:data/command
                                  {:command/kind :command/subscription-resume
                                   :command/data {}}]
                                 core/import-and-refresh]}}
           "Resume subscription"]
          [:small (str "(cancellation for "
                       (subs (:effective_at cancellation-scheduled)
                             0 10) " UTC)")]])

       ;; Actions
       (when active
         [:div {:class "flex items-center space-x-3 mb-4"}
          [:ui/a.link
           {:ui/location
            ;; TODO: adapt to real page:
            {:location/page-id :pages/subscription-change-plan
             :location/params {}}
            :class ui/button-primary}
           "Change Plan"]
          [:button {:class ["text-red-600" "hover:underline"]
                    :on {:click [[:ui/modal-open :modals/subscription-cancel-modal {}]]}}
           "Cancel subscription"]
          ])

       ;; Secondary links (Invoices, Statements)
       (let [class ["text-blue-500" "hover:underline" "mr-4"]]
         [:div
          [:a {:href "/subscription/invoices"
               :target "_blank"
               :class class}
           "Invoices"]
          [:ui/a.link
           {:ui/location
            {:location/page-id :pages/subscription-statement
             :location/params {}}
            :class class}
           "Usage Statements"]
          (when active
            [:a {:href "/subscription/invoices"
                 :target "_blank"
                 :class class}
             "Update Payment"])])])))

(defn render [w]
  (let [w* (-> w
              (part/add-subscription-item)
              (part/add-instagram-accounts))]
    (app-frame/app-frame
      w*
      [:div.mx-auto.max-w-2xl.lg:mx-0.lg:max-w-none

       (if (or (query/loading? w*
                               core/subscription-query)
               (query/loading? w*
                               core/ig-accounts-query)
               (command/issued? w*
                                core/import-paddle-events-command))
         [:span "loading..."]
         (let [subscription (query/get-result w*
                                              core/subscription-query)
               status (:status (:organization/paddle-subscription subscription))
               active (#{"active" "past_due"} status)]
           [:div
            {:class ["flow-root" "max-w-2xl" "space-y-8"]}
            (when-not active
              (list
                (buy/calculator w*)
                #_(buy/call-to-action w*)))
            (when active
              [:dl.space-y-2.divide-y.divide-gray-100.leading-6
               (subscription-overview w*)])
            #_(buy/how-to w*)
            (when active
              (let [suggest-upgrade (:suggest-upgrade (query/get-result w* core/subscription-query))]
                (if (:upgrade? suggest-upgrade)
                  [:div {:class ["shadow-sm"
                                 "rounded-lg"
                                 "bg-yellow-200"
                                 "p-6"
                                 "text-yellow-800"]}
                   [:span.flex.items-center
                    [:i.fa-solid.fa-triangle-exclamation.fa-xl]
                    [:div.ml-4.flex.flex-col
                     [:span [:b "Warning:"]
                      " Your auto top-up balance won't cover the connected Instagram accounts
                      for the remainder of this billing period. Stories will not be posted when your
                      credit balance hits $0"]
                     [:b.my-2 "We suggest upgrading to the following amount:"]]]
                   (buy/upgrade-table w*)]
                  (buy/pricing-table w*))))
            (buy/we-love)
            (when-not active
              [:dl.space-y-2.divide-y.divide-gray-100.leading-6
               (subscription-overview w*)])
            ])
         )
       ])))

(def page
  {:page-id :pages/subscription
   :route [["ui" "subscription"]]
   :on-load (fn [location]
              ;; Funnel step: how many users reach the subscription page,
              ;; and from where (e.g. the onboarding subscribe screen links
              ;; here with ?source=subscribe-screen).
              (client-log/log! :subscription/page-viewed
                               {:subscription/source
                                (or (get-in location
                                            [:location/query-params :source])
                                    "direct")})
              (conj core/query-actions
                    [:subscription/redirect-if-legacy]
                    [:subscription/watch-exit]))
   :render #'render})
